import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CanAccess,
  useDataProvider,
  useGetList,
  useGetMany,
  useNotify,
  useTranslate,
} from "ra-core";
import { Navigate } from "react-router";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { computeSpendStatus } from "../aiCopilot/aiUsage";
import {
  ListCardsSkeleton,
  ListEmptyState,
  ListErrorState,
} from "../design/ListStates";
import { ProgressBar } from "../design/ProgressBar";
import type { CrmDataProvider } from "../providers/types";
import type { AiUsageLogEntry, Deal, Sale } from "../types";
import {
  isCapReached,
  monthHistory,
  parseCapInput,
  spendPercent,
  totalsByAction,
} from "./aiUsageRules";

const RECENT_CALLS_LIMIT = 50;

const formatUsd = (value: number) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value);

const formatNumber = (value: number) =>
  new Intl.NumberFormat("es-MX").format(value);

/** Empty state shared by the three "Uso de IA" sections (sentence + hint, no action). */
const UsageEmptyState = () => {
  const translate = useTranslate();
  return (
    <ListEmptyState
      className="min-h-0 py-4"
      title={translate("crm.aiUsage.no_usage")}
      description={translate("crm.aiUsage.no_usage_hint")}
    />
  );
};

/**
 * Almikia Epic EI (TASK-005): "Uso de IA" -- direccion-only (UI gate here,
 * RLS on `ai_settings` / `ai_usage_log` underneath). Shows month-to-date
 * estimated spend vs the monthly cap, calls/tokens per action, the monthly
 * history and the latest calls. Usage is always shown as counts/cost of calls,
 * never as a ranking or comparison of advisors (independence guide).
 */
export const AiUsageScreen = () => (
  <CanAccess
    resource="ai_usage"
    action="list"
    accessDenied={<Navigate to="/" replace />}
  >
    <AiUsageContent />
  </CanAccess>
);

AiUsageScreen.path = "/ai_usage";

const AiUsageContent = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();

  const settingsQuery = useQuery({
    queryKey: ["ai_usage", "settings"],
    queryFn: () => dataProvider.getAiSettings(),
  });
  const rollupQuery = useQuery({
    queryKey: ["ai_usage", "monthly"],
    queryFn: () => dataProvider.getAiMonthlyUsage(),
  });
  const recent = useGetList<AiUsageLogEntry>("ai_usage_log", {
    pagination: { page: 1, perPage: RECENT_CALLS_LIMIT },
    sort: { field: "created_at", order: "DESC" },
  });

  const calls = recent.data ?? [];
  const salesIds = [...new Set(calls.map((c) => c.sales_id))];
  const dealIds = [
    ...new Set(calls.flatMap((c) => (c.deal_id != null ? [c.deal_id] : []))),
  ];
  const { data: sales } = useGetMany<Sale>(
    "sales",
    { ids: salesIds },
    { enabled: salesIds.length > 0 },
  );
  const { data: deals } = useGetMany<Deal>(
    "deals",
    { ids: dealIds },
    { enabled: dealIds.length > 0 },
  );

  if (settingsQuery.error || rollupQuery.error || recent.error) {
    return (
      <ListErrorState
        message={translate("crm.common.list_error")}
        onRetry={() => {
          settingsQuery.refetch();
          rollupQuery.refetch();
          recent.refetch();
        }}
      />
    );
  }
  if (!settingsQuery.data || !rollupQuery.data || recent.isPending) {
    return <ListCardsSkeleton rows={4} />;
  }

  const settings = settingsQuery.data;
  const rollup = rollupQuery.data;
  const status = computeSpendStatus(rollup, settings);
  const percent = spendPercent(status.spentUsd, status.capUsd);
  const perAction = totalsByAction(rollup, status.month);
  const history = monthHistory(rollup);
  const salesName = new Map(
    (sales ?? []).map((s) => [String(s.id), `${s.first_name} ${s.last_name}`]),
  );
  const dealName = new Map((deals ?? []).map((d) => [String(d.id), d.name]));

  return (
    <div className="mt-2 max-w-2xl mx-auto space-y-4 pb-8">
      <h1 className="px-1 text-xl font-semibold">
        {translate("crm.aiUsage.title")}
      </h1>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-sm text-muted-foreground">
              {translate("crm.aiUsage.spend_label")}
            </span>
            <span className="tabular-nums" data-testid="ai-usage-percent">
              {percent}%
            </span>
          </div>
          <p className="text-2xl font-semibold tabular-nums">
            {translate("crm.aiUsage.spend_of_cap", {
              spent: formatUsd(status.spentUsd),
              cap: formatUsd(status.capUsd),
            })}
          </p>
          <ProgressBar
            value={status.spentUsd}
            max={status.capUsd}
            label={translate("crm.aiUsage.spend_label")}
          />
          <p className="text-sm text-muted-foreground tabular-nums">
            {translate("crm.aiUsage.remaining", {
              remaining: formatUsd(status.remainingUsd),
            })}
          </p>
          {isCapReached(status) ? (
            <Alert>
              <AlertDescription>
                {translate("crm.aiUsage.cap_reached")}
              </AlertDescription>
            </Alert>
          ) : null}
          <p className="text-xs text-muted-foreground">
            {translate("crm.aiUsage.estimate_note")}
          </p>
        </CardContent>
      </Card>

      <CapForm capUsd={settings.monthly_cap_usd} />

      <Card>
        <CardContent className="space-y-2 p-4">
          <h2 className="font-medium">
            {translate("crm.aiUsage.per_action_title")}
          </h2>
          {perAction.length === 0 ? (
            <UsageEmptyState />
          ) : (
            <ul className="divide-y text-sm">
              {perAction.map((row) => (
                <li
                  key={row.action}
                  className="flex items-center justify-between gap-2 py-2"
                >
                  <span>{translate(`crm.aiUsage.actions.${row.action}`)}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {translate("crm.aiUsage.calls_tokens", {
                      smart_count: row.calls,
                      calls: formatNumber(row.calls),
                      tokens: formatNumber(row.tokens),
                    })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-2 p-4">
          <h2 className="font-medium">
            {translate("crm.aiUsage.history_title")}
          </h2>
          {history.length === 0 ? <UsageEmptyState /> : null}
          <ul className="divide-y text-sm">
            {history.map((row) => (
              <li
                key={row.month}
                className="flex items-center justify-between gap-2 py-2"
              >
                <span>{row.month.slice(0, 7)}</span>
                <span className="tabular-nums text-muted-foreground">
                  {translate("crm.aiUsage.history_row", {
                    cost: formatUsd(row.costUsd),
                    smart_count: row.calls,
                    calls: formatNumber(row.calls),
                    tokens: formatNumber(row.tokens),
                  })}
                </span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-2 p-4">
          <h2 className="font-medium">
            {translate("crm.aiUsage.recent_title")}
          </h2>
          {calls.length === 0 ? (
            <UsageEmptyState />
          ) : (
            <ul className="divide-y text-sm">
              {calls.map((call) => (
                <li key={call.id} className="space-y-0.5 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">
                      {translate(`crm.aiUsage.actions.${call.action}`)}
                    </span>
                    <span className="tabular-nums">
                      {formatUsd(call.estimated_cost_usd)}
                    </span>
                  </div>
                  <p className="text-muted-foreground">
                    {new Date(call.created_at).toLocaleString("es-MX")}
                    {" · "}
                    {salesName.get(String(call.sales_id)) ?? "—"}
                    {" · "}
                    {(call.deal_id != null &&
                      dealName.get(String(call.deal_id))) ||
                      "—"}
                    {" · "}
                    {translate(`crm.aiUsage.statuses.${call.status}`)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const CapForm = ({ capUsd }: { capUsd: number }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [text, setText] = useState(String(capUsd));
  const [invalid, setInvalid] = useState(false);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const value = parseCapInput(text);
    setInvalid(value === null);
    if (value === null) return;
    setSaving(true);
    try {
      await dataProvider.updateAiSettings({ monthly_cap_usd: value });
      await queryClient.invalidateQueries({ queryKey: ["ai_usage"] });
      notify("crm.aiUsage.cap_saved", { type: "success" });
    } catch {
      notify("crm.aiUsage.cap_error", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent className="p-4">
        <form onSubmit={onSubmit} className="space-y-2" noValidate>
          <Label htmlFor="ai-usage-cap">
            {translate("crm.aiUsage.cap_label")}
          </Label>
          <div className="flex gap-2">
            <Input
              id="ai-usage-cap"
              className="h-11"
              inputMode="decimal"
              value={text}
              aria-invalid={invalid}
              onChange={(e) => setText(e.target.value)}
            />
            <Button type="submit" className="min-h-11" disabled={saving}>
              {translate("ra.action.save")}
            </Button>
          </div>
          {invalid ? (
            <p role="alert" className="text-sm text-destructive">
              {translate("crm.aiUsage.cap_invalid")}
            </p>
          ) : null}
        </form>
      </CardContent>
    </Card>
  );
};
